<template>
  <div class="project-container">
    <header class="header">
      <div class="flex-container">
        <div class="image-container">
          <img :src="require('@/assets/image/portfolio-screen.png')" alt="Logo Portfolio" class="header-image" loading="lazy"/>
        </div>
        <div class="text-container">
          <h1>{{ $t('portfolio.title') }}</h1>
          <p class="subtitle">{{ $t('portfolio.subtitle') }}</p>
        </div>
      </div>
    </header>

    <section class="about-project">
      <h2>{{ $t('portfolio.aboutTitle') }}</h2>
      <div class="content-grid">
        <div class="text-block">
          <p v-html="$t('portfolio.about1')"></p>
          <p v-html="$t('portfolio.about2')"></p>
        </div>
        <div class="highlight-box">
           <div class="stat-item">
             <span class="mdi mdi-vuejs stat-icon"></span>
             <span class="stat-text">Vue.js 3 SPA</span>
           </div>
           <div class="stat-item">
             <span class="mdi mdi-language-typescript stat-icon"></span>
             <span class="stat-text">TypeScript Core</span>
           </div>
           <div class="stat-item">
             <span class="mdi mdi-kubernetes stat-icon"></span>
             <span class="stat-text">K8s Deployment</span>
           </div>
        </div>
      </div>
    </section>

    <section class="frontend-details">
      <h2>{{ $t('portfolio.frontendTitle') }}</h2>
      <div class="tech-grid">
        <div class="tech-card">
          <div class="tech-header">
            <span class="mdi mdi-layers-outline tech-icon"></span>
            <h4>Tech Stack & Architecture</h4>
          </div>
          <div class="feature-list">
            <div class="feature-item">
              <span class="mdi mdi-check-circle-outline feature-icon"></span>
              <p v-html="$t('portfolio.frontend1')"></p>
            </div>
            <div class="feature-item">
              <span class="mdi mdi-palette-swatch-outline feature-icon"></span>
              <p v-html="$t('portfolio.frontend2')"></p>
            </div>
            <div class="feature-item">
              <span class="mdi mdi-animation-outline feature-icon"></span>
              <p v-html="$t('portfolio.frontend3')"></p>
            </div>
          </div>
        </div>
        
        <div class="image-wrapper-bordered">
           <img :src="require('@/assets/image/portfolio-screen.png')" alt="Portfolio Interface Preview" class="doc-image-contained" loading="lazy"/>
        </div>
      </div>
    </section>

    <section class="architecture-details">
      <h2>{{ $t('portfolio.gitopsTitle') }}</h2>
      <div class="tech-grid">
        <div class="tech-card">
          <div class="tech-header">
            <span class="mdi mdi-gitlab tech-icon"></span>
            <h4>CI Pipeline (GitLab)</h4>
          </div>
          <div class="feature-list">
            <div class="feature-item">
              <span class="mdi mdi-hammer-wrench feature-icon"></span>
              <p v-html="$t('portfolio.gitops1')"></p>
            </div>
            <div class="feature-item">
              <span class="mdi mdi-docker feature-icon"></span>
              <p v-html="$t('portfolio.gitops3')"></p>
            </div>
          </div>
        </div>
        
        <div class="tech-card">
          <div class="tech-header">
            <span class="mdi mdi-sync tech-icon"></span>
            <h4>CD Workflow (ArgoCD)</h4>
          </div>
          <div class="feature-list">
            <div class="feature-item">
              <span class="mdi mdi-refresh feature-icon"></span>
              <p v-html="$t('portfolio.gitops2')"></p>
            </div>
            <div class="feature-item">
              <span class="mdi mdi-shield-check-outline feature-icon"></span>
              <p>Validation automatique de l'état du cluster via GitOps.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="visuals-section">
      <h2>Infrastructure & GitOps View</h2>
      <div class="visuals-grid">
        <div class="visual-card">
          <h3>ArgoCD Dashboard</h3>
          <div class="image-wrapper">
            <img :src="require('@/assets/image/argocd-portfolio.png')" alt="ArgoCD View" class="doc-image" loading="lazy"/>
          </div>
        </div>
        <div class="tech-card">
          <div class="tech-header">
            <span class="mdi mdi-kubernetes tech-icon"></span>
            <h4>Cloud-Native Stack</h4>
          </div>
          <div class="feature-list">
            <div class="feature-item">
              <span class="mdi mdi-security feature-icon"></span>
              <p v-html="$t('portfolio.k8s1')"></p>
            </div>
            <div class="feature-item">
              <span class="mdi mdi-heart-pulse feature-icon"></span>
              <p v-html="$t('portfolio.k8s2')"></p>
            </div>
            <div class="feature-item">
              <span class="mdi mdi-chart-areaspline feature-icon"></span>
              <p v-html="$t('portfolio.k8s3')"></p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="technical-details">
      <h2>{{ $t('portfolio.learningsTitle') }}</h2>
      <div class="competencies-grid">
        <InfraCompetencyCard 
          icon="mdi-rocket-launch-outline"
          title="CI/CD Pipelines"
          :description="$t('portfolio.learning1')"
        />
        <InfraCompetencyCard 
          icon="mdi-kubernetes"
          title="K8s Manifests"
          :description="$t('portfolio.learning2')"
        />
        <InfraCompetencyCard 
          icon="mdi-certificate-outline"
          title="Cert-Manager"
          :description="$t('portfolio.learning3')"
        />
        <InfraCompetencyCard 
          icon="mdi-sync"
          title="GitOps Automation"
          :description="$t('portfolio.learning4')"
        />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';
import InfraCompetencyCard from '@/component/project/infra/InfraCompetencyCard.vue';

@Component({
  components: {
    InfraCompetencyCard
  }
})
export default class PortfolioComponent extends Vue {
}
</script>

<style scoped>
.project-container {
  font-family: 'TTInterfaces', sans-serif;
  line-height: 1.7;
  color: var(--ln-text-primary);
  background-color: transparent;
  width: 100%;
  max-width: var(--ln-content-max-width);
  margin: 0 auto;
  padding: 2rem 1.5rem;
  box-sizing: border-box;
}

.header {
  text-align: center;
  margin-bottom: 2rem;
  padding: 2.5rem;
  background: var(--ln-glass-elevated);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border-radius: var(--ln-radius-lg);
  border: 1px solid var(--ln-glass-border);
  box-shadow: 0 12px 48px var(--ln-glass-shadow);
}

.flex-container {
  display: flex;
  align-items: center;
  gap: 3rem;
  flex-wrap: wrap;
  justify-content: center;
}

.image-container {
  width: 280px;
  flex-shrink: 0;
}

.text-container {
  flex: 1 1 400px;
  text-align: left;
}

.header h1 {
  font-size: clamp(2rem, 6vw, 3rem);
  font-weight: 700;
  color: var(--ln-text-primary);
  margin-bottom: 0.5rem;
  letter-spacing: -1px;
}

.subtitle {
  font-size: 1.25rem;
  color: var(--ln-text-secondary);
  font-weight: 500;
  opacity: 0.9;
}

.header-image {
  width: 100%;
  max-width: 280px;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  transition: transform 0.5s ease;
}

.header-image:hover {
  transform: scale(1.05) rotate(2deg);
}

section {
  margin-bottom: 2rem;
  padding: 2.5rem;
  background: var(--ln-glass-base);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  box-shadow: 0 8px 32px var(--ln-glass-shadow);
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
}

section:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 50px var(--ln-glass-shadow-elevated);
}

h2 {
  font-size: clamp(1.5rem, 5vw, 2.2rem);
  font-weight: 700;
  color: var(--ln-text-primary);
  margin-bottom: 2rem;
  position: relative;
  padding-bottom: 1rem;
}

h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 80px;
  height: 4px;
  background: linear-gradient(to right, var(--ln-teal-deep), var(--ln-terracotta-subtle));
  border-radius: 2px;
}

.content-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 3rem;
  align-items: center;
}

.highlight-box {
  background: var(--ln-glass-surface);
  padding: 2rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border-strong);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-item:last-child { margin-bottom: 0; }
.stat-icon { font-size: 2.2rem; color: var(--ln-teal-deep); }
.stat-text { font-weight: 600; color: var(--ln-text-primary); }

.tech-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.tech-card {
  display: flex;
  flex-direction: column;
  background: var(--ln-glass-surface);
  padding: 2rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
}

.tech-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--ln-glass-border);
}

.tech-icon { font-size: 2.5rem; color: var(--ln-teal-deep); }
.tech-header h4 { margin: 0; color: var(--ln-text-primary); font-size: 1.4rem; }

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.feature-icon {
  font-size: 1.5rem;
  color: var(--ln-teal-light);
  background: var(--ln-glass-elevated);
  padding: 0.5rem;
  border-radius: 50%;
  box-shadow: inset 0 2px 4px var(--ln-glass-shadow);
  flex-shrink: 0;
}

.feature-item p {
  margin: 0;
  font-size: 0.95rem;
  color: var(--ln-text-secondary);
  line-height: 1.6;
}

/* Visuals grid */
.visuals-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 2rem;
}

.image-wrapper-bordered {
  background: var(--ln-glass-surface);
  padding: 1rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.image-wrapper {
  background: var(--ln-glass-surface);
  padding: 1rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.doc-image {
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 8px 24px var(--ln-glass-shadow);
  transition: transform 0.4s var(--ln-ease-smooth);
}

.doc-image-contained {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  transition: transform 0.4s var(--ln-ease-smooth);
}

.doc-image:hover, .doc-image-contained:hover {
  transform: scale(1.02);
}

.competencies-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

@media (max-width: 1200px) {
  .competencies-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 992px) {
  .content-grid, .tech-grid, .visuals-grid { grid-template-columns: 1fr; }
}

@media (max-width: 768px) {
  .header { padding: 1.5rem; }
  .flex-container { gap: 1rem; flex-direction: column; }
  .text-container { text-align: center; flex: 1 1 auto; }
  section { padding: 1.5rem; }
  .visuals-grid { display: flex; flex-direction: column; }
  .competencies-grid { grid-template-columns: 1fr; }
}
</style>
